import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/auth/AuthContext";
import { Navigate } from "react-router-dom";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [timezones, setTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState("UTC");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (isAdmin) {
      loadSettings();
    }
  }, [isAdmin]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load timezone options
      const tzRes = await api.get("/settings/timezones");
      setTimezones(tzRes.data.timezones || []);

      // Load current settings
      const settingsRes = await api.get("/settings");
      setSelectedTimezone(settingsRes.data.settings?.timezone || "UTC");
    } catch (e) {
      setError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await api.put("/settings", { timezone: selectedTimezone });
      setSuccess("Settings saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Get the selected timezone label for display
  const selectedTzLabel = timezones.find((tz) => tz.value === selectedTimezone)?.label || selectedTimezone;

  return (
    <div data-testid="settings-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
      <TopNav />
      <div className="mx-auto max-w-2xl px-4 py-8 space-y-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="settings-title">
            Settings
          </h1>
          <p className="mt-2 text-base text-slate-600" data-testid="settings-subtitle">
            Configure app preferences
          </p>
        </div>

        {error && (
          <div data-testid="settings-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            {error}
          </div>
        )}

        {success && (
          <div data-testid="settings-success" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
            {success}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-slate-600">Loading settings...</div>
        ) : (
          <>
            {/* Timezone Setting */}
            <Card data-testid="timezone-card" className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle data-testid="timezone-title">Display Timezone</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                    <SelectTrigger data-testid="timezone-select" className="bg-white border-slate-300">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {timezones.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value} data-testid={`tz-option-${tz.value}`}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-600">
                    This affects all date/time displays in the app and Excel exports.
                  </p>
                </div>

                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-sm font-medium text-slate-900 mb-2">Timezone will apply to:</div>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li className="flex items-center gap-2">
                      <span className="text-emerald-500">✓</span>
                      <span>Dashboard session list</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-emerald-500">✓</span>
                      <span>Session detail page</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-emerald-500">✓</span>
                      <span>Reports page table</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-emerald-500">✓</span>
                      <span>Excel export (times converted)</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Google Drive Status */}
            <Card data-testid="drive-status-card" className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle data-testid="drive-status-title">Google Drive</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <span className="text-emerald-500">✓</span>
                  <span className="text-sm text-slate-700">Connected and configured</span>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              data-testid="save-settings-btn"
              onClick={handleSave}
              disabled={saving}
              className="w-full rounded-full bg-slate-900 text-white hover:bg-slate-800"
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
